import type { SafeInsets } from "../platform/types";
import { uiScale, scaleRect, unscaleSafe, type ButtonRect } from "./layout";
import { clamp01, easeOutBack } from "./visual-motion";
export function resultRects(
  width: number,
  height: number,
  safe: SafeInsets,
): {
  ribbon: ButtonRect;
  subtitle: ButtonRect;
  chips: ButtonRect[];
  rewards: ButtonRect;
  playAgain: ButtonRect;
  home: ButtonRect;
} {
  const s = uiScale(width, height);
  if (s > 1) {
    const base = resultRects(width / s, height / s, unscaleSafe(safe, s));
    return {
      ribbon: scaleRect(base.ribbon, s),
      subtitle: scaleRect(base.subtitle, s),
      chips: base.chips.map((r) => scaleRect(r, s)),
      rewards: scaleRect(base.rewards, s),
      playAgain: scaleRect(base.playAgain, s),
      home: scaleRect(base.home, s),
    };
  }
  const scale = Math.min(width - safe.left - safe.right, 390) / 412,
    C = width / 2,
    B = height - safe.bottom;
  const rect = (x: number, y: number, w: number, h: number): ButtonRect => ({
    x: C + (x - 206) * scale,
    y,
    width: w * scale,
    height: h,
  });
  const compact = height - safe.top - safe.bottom < 720;
  if (width > height && compact) {
    // A stacked portrait footer leaves no room between the subtitle and chips.
    // Share the landscape footer between rewards and actions instead.
    const columnWidth = Math.min(
      390,
      (width - safe.left - safe.right - 48) / 2,
    );
    const left = C - columnWidth - 12;
    const right = C + 12;
    return {
      ribbon: rect(56, safe.top + 16, 300, 60),
      subtitle: rect(16, safe.top + 82, 380, 30),
      chips: [0, 1, 2].map((i) => ({
        x: left + (i * columnWidth) / 3,
        y: B - 130,
        width: columnWidth / 3 - 8,
        height: 34,
      })),
      rewards: { x: left, y: B - 88, width: columnWidth, height: 84 },
      playAgain: { x: right, y: B - 142, width: columnWidth, height: 68 },
      home: { x: right, y: B - 60, width: columnWidth, height: 44 },
    };
  }
  // Retain room for the shared draw podium on the shortest viewport.
  const footerOffset =
    height < 660
      ? 14
      : height < 740
        ? 2
        : compact
          ? 8
          : safe.top + safe.bottom > 0 && height - safe.top - safe.bottom < 770
            ? 2
            : 0;
  return {
    ribbon: rect(56, safe.top + (compact ? 16 : 40), 300, 60),
    subtitle: rect(16, safe.top + (compact ? 82 : 110), 380, 30),
    chips: [14, 145, 276].map((x) =>
      rect(x, B - (height < 740 ? 291 : 335) + footerOffset, 122, 54),
    ),
    rewards: rect(
      14,
      B - (height < 740 ? 229 : 267) + footerOffset,
      384,
      height < 740 ? 84 : 100,
    ),
    playAgain: {
      x: C - Math.max(332 * scale, width * 0.76) / 2,
      y: B - 147 + footerOffset,
      width: Math.max(332 * scale, width * 0.76),
      height: 68,
    },
    home: rect(130, B - 61 + footerOffset, 152, 44),
  };
}
export function resultIntro(t: number, outcome: string, reduced = false) {
  const start = 120,
    duration = 300,
    p = clamp01((t - start) / duration),
    win = outcome === "win";
  const squash =
    reduced || t < 420 || t > 520 ? 0 : Math.sin(((t - 420) / 100) * Math.PI);
  return {
    visible: reduced || t >= start,
    opacity: reduced ? clamp01(t / 150) : 1,
    scale: reduced
      ? 1
      : (win ? 2.4 : 1.6) -
        ((win ? 2.4 : 1.6) - 1) * easeOutBack(p, win ? 1.8 : 1.2),
    x: 1 + 0.1 * squash,
    y: 1 - 0.1 * squash,
    subtitle: reduced ? 1 : clamp01((t - 520) / 240),
    dance: Math.max(0, t - 300),
  };
}
export function victoryPose(t: number, reduced = false) {
  const p = Math.max(0, t) % 1600;
  if (reduced) return { hop: 0, stretch: 0, spin: 0, wave: 0 };
  const hop =
    p < 190
      ? 0.22 * (1 - (1 - p / 190) ** 2)
      : p < 380
        ? 0.22 * (1 - ((p - 190) / 190) ** 2)
        : 0;
  const stretch =
    p >= 380 && p < 470 ? Math.sin(((p - 380) / 90) * Math.PI) : 0;
  const q = clamp01((p - 470) / 520),
    ease = q < 0.5 ? 4 * q ** 3 : 1 - (-2 * q + 2) ** 3 / 2;
  return {
    hop,
    stretch,
    spin: 2 * Math.PI * ease,
    wave: p >= 990 && p < 1590 ? Math.sin(((p - 990) / 600) * 6 * Math.PI) : 0,
  };
}

export interface ResultCopyInput {
  outcome: "win" | "lose" | "draw";
  winType: string;
  playerHP: number;
  opponentHP: number;
  hits?: number;
  round?: number;
  score?: [number, number];
  opponentName?: string; // snowman just fought
  nextOpponentName?: string; // snowman to face next (progression)
}
export function resultCopy(r: ResultCopyInput) {
  // Names are additive: copy is unchanged unless the caller passes them.
  return {
    ...baseResultCopy(r),
    ...(r.opponentName ? { opponentName: r.opponentName } : {}),
    ...(r.nextOpponentName ? { nextLine: `Next: ${r.nextOpponentName}` } : {}),
  };
}
function baseResultCopy(r: ResultCopyInput) {
  const diff = Math.abs(r.opponentHP - r.playerHP);
  if (r.winType === "forfeit")
    return { headline: "MATCH LEFT", subtitle: "No rewards this time" };
  if (r.outcome === "draw")
    return { headline: "DRAW!", subtitle: "Evenly matched!" };
  if (r.outcome === "win")
    return {
      headline: "YOU WIN!",
      subtitle:
        r.winType === "KO"
          ? `K.O. in Round ${r.round ?? 3}!`
          : diff <= 20
            ? "CLOSE CALL!"
            : `Won ${r.score?.[0] ?? 2}-${r.score?.[1] ?? 1}!`,
    };
  if (r.winType === "KO" || r.playerHP <= 0 || diff > 50)
    return { headline: "GOOD FIGHT!", subtitle: "Practice your blocks!" };
  return diff <= 20
    ? { headline: "SO CLOSE!", subtitle: `Lost by ${diff} HP. One more?` }
    : {
        headline: "NICE TRY!",
        subtitle: r.hits
          ? `You landed ${r.hits} ${r.hits === 1 ? "hit" : "hits"}!`
          : "Keep swinging!",
      };
}
export function resultRewards(r: ResultCopyInput) {
  if (r.winType === "forfeit") return { coins: 0, xp: 0, chest: 0 };
  if (r.outcome === "draw") return { coins: 60, xp: 30, chest: 0 };
  if (r.outcome === "win")
    return {
      coins: r.winType === "KO" ? 120 : 100,
      xp: r.winType === "KO" ? 60 : 50,
      chest: 1,
    };
  const heavy =
    r.winType === "KO" || r.playerHP <= 0 || r.opponentHP - r.playerHP > 50;
  return { coins: heavy ? 30 : 40, xp: heavy ? 15 : 20, chest: 0 };
}
export function rewardTimeline(
  t: number,
  reduced = false,
  forfeit = false,
  reward = { coins: 120, xp: 60 },
) {
  const progress = clamp01((t - 1000) / 900),
    ease = 1 - (1 - progress) ** 3;
  const cta = clamp01((t - 2100) / 300);
  return {
    coinPulse:
      reduced || t < 1000 || t >= 1900
        ? 1
        : 1 + 0.08 * Math.sin((((120 * ease) % 10) / 10) * Math.PI),
    coins: Math.round((forfeit ? 0 : reward.coins) * ease),
    xp: forfeit
      ? 0
      : Math.round(reward.xp * (1 - (1 - clamp01((t - 1000) / 700)) ** 3)),
    xpFill: forfeit
      ? 0.55
      : 0.55 +
        (reward.xp / 60) * 0.17 * (1 - (1 - clamp01((t - 1000) / 700)) ** 3),
    chips: [0, 1, 2].map((i) => ({
      visible: t >= 700 + i * 90,
      scale: reduced ? 1 : 0.4 + 0.6 * easeOutBack((t - 700 - i * 90) / 300, 2),
    })),
    chest:
      reduced || t < 1900 || t > 2200
        ? 0
        : (Math.sin(((t - 1900) / 300) * 6 * Math.PI) * Math.PI) / 30,
    playVisible: t >= 2100,
    playY: reduced ? 0 : 40 * (1 - easeOutBack(cta, 1.6)),
    playAlpha: reduced ? clamp01((t - 2100) / 150) : 1,
    homeAlpha: clamp01((t - 2200) / 200),
    pulse:
      reduced || t < 2400
        ? 1
        : 1.025 - 0.025 * Math.cos(((t - 2400) / 1200) * 2 * Math.PI),
  };
}
export function resultPlayRect(
  width: number,
  height: number,
  safe: SafeInsets,
  t: number,
  reduced = false,
): ButtonRect {
  const base = resultRects(width, height, safe).playAgain,
    m = rewardTimeline(t, reduced);
  return {
    x: base.x + (base.width - base.width * m.pulse) / 2,
    y: base.y + m.playY + (base.height - base.height * m.pulse) / 2,
    width: base.width * m.pulse,
    height: base.height * m.pulse,
  };
}
